/*!
 * @license
 * Copyright © 2005-2024 Hyland Software, Inc. and its affiliates. All rights reserved.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
import { __awaiter } from "tslib";
import { ChildAssociationEntry, DirectAccessUrlEntry, NodeAssociationPaging, NodeBodyCreate, NodeChildAssociationPaging, NodeEntry } from '../model';
import { BaseApi } from './base.api';
import { throwIfNotDefined } from '../../../assert';
import { buildCollectionParam } from '../../../alfrescoApiClient';
export class NodesApi extends BaseApi {
    copyNode(nodeId, nodeBodyCopy, opts) {
        throwIfNotDefined(nodeId, 'nodeId');
        throwIfNotDefined(nodeBodyCopy, 'nodeBodyCopy');
        const pathParams = {
            nodeId
        };
        const queryParams = {
            include: buildCollectionParam(opts === null || opts === void 0 ? void 0 : opts.include, 'csv'),
            fields: buildCollectionParam(opts === null || opts === void 0 ? void 0 : opts.fields, 'csv')
        };
        return this.post({
            path: '/nodes/{nodeId}/copy',
            pathParams,
            queryParams,
            bodyParam: nodeBodyCopy,
            returnType: NodeEntry
        });
    }
    createAssociation(nodeId, associationBodyCreate, opts) {
        throwIfNotDefined(nodeId, 'nodeId');
        throwIfNotDefined(associationBodyCreate, 'associationBodyCreate');
        const pathParams = {
            nodeId
        };
        const queryParams = {
            fields: buildCollectionParam(opts === null || opts === void 0 ? void 0 : opts.fields, 'csv')
        };
        return this.post({
            path: '/nodes/{nodeId}/targets',
            pathParams,
            queryParams,
            bodyParam: associationBodyCreate
        });
    }
    createNode(nodeId, nodeBodyCreate, opts, formParams) {
        throwIfNotDefined(nodeId, 'nodeId');
        throwIfNotDefined(nodeBodyCreate, 'nodeBodyCreate');
        const pathParams = {
            nodeId
        };
        const queryParams = {
            autoRename: opts === null || opts === void 0 ? void 0 : opts.autoRename,
            include: buildCollectionParam(opts === null || opts === void 0 ? void 0 : opts.include, 'csv'),
            fields: buildCollectionParam(opts === null || opts === void 0 ? void 0 : opts.fields, 'csv')
        };
        formParams = formParams || {};
        let contentTypes;
        let returnType;
        if (formParams.filedata) {
            contentTypes = ['multipart/form-data'];
        }
        else {
            contentTypes = ['application/json'];
            returnType = NodeEntry;
        }
        return this.post({
            path: '/nodes/{nodeId}/children',
            pathParams,
            queryParams,
            formParams,
            bodyParam: nodeBodyCreate,
            contentTypes,
            returnType
        });
    }
    createFolder(name, relativePath, nodeId, opts) {
        nodeId = nodeId || '-root-';
        const nodeBody = new NodeBodyCreate({
            name,
            relativePath,
            nodeType: 'cm:folder'
        });
        return this.createNode(nodeId, nodeBody, opts);
    }
    createSecondaryChildAssociation(nodeId, secondaryChildAssociationBodyCreate, opts) {
        throwIfNotDefined(nodeId, 'nodeId');
        throwIfNotDefined(secondaryChildAssociationBodyCreate, 'secondaryChildAssociationBodyCreate');
        const pathParams = {
            nodeId
        };
        const queryParams = {
            fields: buildCollectionParam(opts === null || opts === void 0 ? void 0 : opts.fields, 'csv')
        };
        return this.post({
            path: '/nodes/{nodeId}/secondary-children',
            pathParams,
            queryParams,
            bodyParam: secondaryChildAssociationBodyCreate,
            returnType: ChildAssociationEntry
        });
    }
    deleteAssociation(nodeId, targetId, opts) {
        throwIfNotDefined(nodeId, 'nodeId');
        throwIfNotDefined(targetId, 'targetId');
        const pathParams = {
            nodeId,
            targetId
        };
        const queryParams = {
            assocType: opts === null || opts === void 0 ? void 0 : opts.assocType
        };
        return this.delete({
            path: '/nodes/{nodeId}/targets/{targetId}',
            pathParams,
            queryParams
        });
    }
    deleteNode(nodeId, opts) {
        throwIfNotDefined(nodeId, 'nodeId');
        const pathParams = {
            nodeId
        };
        const queryParams = {
            permanent: opts === null || opts === void 0 ? void 0 : opts.permanent
        };
        return this.delete({
            path: '/nodes/{nodeId}',
            pathParams,
            queryParams
        });
    }
    deleteNodes(nodeIds, opts) {
        return __awaiter(this, void 0, void 0, function* () {
            throwIfNotDefined(nodeIds, 'nodeIds');
            const promises = [];
            for (const id of nodeIds) {
                promises.push(yield this.deleteNode(id, opts));
            }
            return Promise.all(promises);
        });
    }
    deleteSecondaryChildAssociation(nodeId, childId, opts) {
        throwIfNotDefined(nodeId, 'nodeId');
        throwIfNotDefined(childId, 'childId');
        const pathParams = {
            nodeId,
            childId
        };
        return this.delete({
            path: '/nodes/{nodeId}/secondary-children/{childId}',
            pathParams,
            queryParams: opts
        });
    }
    getNode(nodeId, opts) {
        throwIfNotDefined(nodeId, 'nodeId');
        const pathParams = {
            nodeId
        };
        const queryParams = {
            include: buildCollectionParam(opts === null || opts === void 0 ? void 0 : opts.include, 'csv'),
            relativePath: opts === null || opts === void 0 ? void 0 : opts.relativePath,
            fields: buildCollectionParam(opts === null || opts === void 0 ? void 0 : opts.fields, 'csv')
        };
        return this.get({
            path: '/nodes/{nodeId}',
            pathParams,
            queryParams,
            returnType: NodeEntry
        });
    }
    getNodeContent(nodeId, opts) {
        var _a;
        throwIfNotDefined(nodeId, 'nodeId');
        opts = opts || {};
        const pathParams = {
            nodeId
        };
        const queryParams = {
            attachment: (_a = opts === null || opts === void 0 ? void 0 : opts.attachment) !== null && _a !== void 0 ? _a : null
        };
        const headerParams = {};
        if (opts === null || opts === void 0 ? void 0 : opts.ifModifiedSince) {
            Object.defineProperty(headerParams, 'If-Modified-Since', { value: opts === null || opts === void 0 ? void 0 : opts.ifModifiedSince });
        }
        if (opts === null || opts === void 0 ? void 0 : opts.range) {
            Object.defineProperty(headerParams, 'Range', { value: opts === null || opts === void 0 ? void 0 : opts.range });
        }
        const accepts = ['application/octet-stream'];
        return this.get({
            path: '/nodes/{nodeId}/content',
            pathParams,
            queryParams,
            headerParams,
            accepts,
            returnType: 'blob'
        });
    }
    listNodeChildren(nodeId, opts) {
        throwIfNotDefined(nodeId, 'nodeId');
        const pathParams = {
            nodeId
        };
        const queryParams = {
            skipCount: opts === null || opts === void 0 ? void 0 : opts.skipCount,
            maxItems: opts === null || opts === void 0 ? void 0 : opts.maxItems,
            orderBy: buildCollectionParam(opts === null || opts === void 0 ? void 0 : opts.orderBy, 'csv'),
            where: opts === null || opts === void 0 ? void 0 : opts.where,
            include: buildCollectionParam(opts === null || opts === void 0 ? void 0 : opts.include, 'csv'),
            relativePath: opts === null || opts === void 0 ? void 0 : opts.relativePath,
            includeSource: opts === null || opts === void 0 ? void 0 : opts.includeSource,
            fields: buildCollectionParam(opts === null || opts === void 0 ? void 0 : opts.fields, 'csv')
        };
        return this.get({
            path: '/nodes/{nodeId}/children',
            pathParams,
            queryParams,
            returnType: NodeChildAssociationPaging
        });
    }
    listParents(nodeId, opts) {
        throwIfNotDefined(nodeId, 'nodeId');
        opts = opts || {};
        const pathParams = {
            nodeId
        };
        const queryParams = {
            where: opts === null || opts === void 0 ? void 0 : opts.where,
            include: buildCollectionParam(opts === null || opts === void 0 ? void 0 : opts.include, 'csv'),
            skipCount: opts === null || opts === void 0 ? void 0 : opts.skipCount,
            maxItems: opts === null || opts === void 0 ? void 0 : opts.maxItems,
            includeSource: opts === null || opts === void 0 ? void 0 : opts.includeSource,
            fields: buildCollectionParam(opts === null || opts === void 0 ? void 0 : opts.fields, 'csv')
        };
        return this.get({
            path: '/nodes/{nodeId}/parents',
            pathParams,
            queryParams,
            returnType: NodeAssociationPaging
        });
    }
    listSecondaryChildren(nodeId, opts) {
        throwIfNotDefined(nodeId, 'nodeId');
        opts = opts || {};
        const pathParams = {
            nodeId
        };
        const queryParams = {
            where: opts === null || opts === void 0 ? void 0 : opts.where,
            include: buildCollectionParam(opts === null || opts === void 0 ? void 0 : opts.include, 'csv'),
            skipCount: opts === null || opts === void 0 ? void 0 : opts.skipCount,
            maxItems: opts === null || opts === void 0 ? void 0 : opts.maxItems,
            includeSource: opts === null || opts === void 0 ? void 0 : opts.includeSource,
            fields: buildCollectionParam(opts === null || opts === void 0 ? void 0 : opts.fields, 'csv')
        };
        return this.get({
            path: '/nodes/{nodeId}/secondary-children',
            pathParams,
            queryParams,
            returnType: NodeChildAssociationPaging
        });
    }
    listSourceAssociations(nodeId, opts) {
        throwIfNotDefined(nodeId, 'nodeId');
        opts = opts || {};
        const pathParams = {
            nodeId
        };
        const queryParams = {
            where: opts === null || opts === void 0 ? void 0 : opts.where,
            include: buildCollectionParam(opts === null || opts === void 0 ? void 0 : opts.include, 'csv'),
            fields: buildCollectionParam(opts === null || opts === void 0 ? void 0 : opts.fields, 'csv')
        };
        return this.get({
            path: '/nodes/{nodeId}/sources',
            pathParams,
            queryParams,
            returnType: NodeAssociationPaging
        });
    }
    listTargetAssociations(nodeId, opts) {
        throwIfNotDefined(nodeId, 'nodeId');
        opts = opts || {};
        const pathParams = {
            nodeId
        };
        const queryParams = {
            skipCount: opts === null || opts === void 0 ? void 0 : opts.skipCount,
            maxItems: opts === null || opts === void 0 ? void 0 : opts.maxItems,
            where: opts === null || opts === void 0 ? void 0 : opts.where,
            include: buildCollectionParam(opts === null || opts === void 0 ? void 0 : opts.include, 'csv'),
            fields: buildCollectionParam(opts === null || opts === void 0 ? void 0 : opts.fields, 'csv')
        };
        return this.get({
            path: '/nodes/{nodeId}/targets',
            pathParams,
            queryParams,
            returnType: NodeAssociationPaging
        });
    }
    lockNode(nodeId, nodeBodyLock, opts) {
        throwIfNotDefined(nodeId, 'nodeId');
        throwIfNotDefined(nodeBodyLock, 'nodeBodyLock');
        const pathParams = {
            nodeId
        };
        const queryParams = {
            include: buildCollectionParam(opts === null || opts === void 0 ? void 0 : opts.include, 'csv'),
            fields: buildCollectionParam(opts === null || opts === void 0 ? void 0 : opts.fields, 'csv')
        };
        return this.post({
            path: '/nodes/{nodeId}/lock',
            pathParams,
            queryParams,
            bodyParam: nodeBodyLock,
            returnType: NodeEntry
        });
    }
    moveNode(nodeId, nodeBodyMove, opts) {
        throwIfNotDefined(nodeId, 'nodeId');
        throwIfNotDefined(nodeBodyMove, 'nodeBodyMove');
        const pathParams = {
            nodeId
        };
        const queryParams = {
            include: buildCollectionParam(opts === null || opts === void 0 ? void 0 : opts.include, 'csv'),
            fields: buildCollectionParam(opts === null || opts === void 0 ? void 0 : opts.fields, 'csv')
        };
        return this.post({
            path: '/nodes/{nodeId}/move',
            pathParams,
            queryParams,
            bodyParam: nodeBodyMove,
            returnType: NodeEntry
        });
    }
    unlockNode(nodeId, opts) {
        throwIfNotDefined(nodeId, 'nodeId');
        const pathParams = {
            nodeId
        };
        const queryParams = {
            include: buildCollectionParam(opts === null || opts === void 0 ? void 0 : opts.include, 'csv'),
            fields: buildCollectionParam(opts === null || opts === void 0 ? void 0 : opts.fields, 'csv')
        };
        return this.post({
            path: '/nodes/{nodeId}/unlock',
            pathParams,
            queryParams,
            returnType: NodeEntry
        });
    }
    updateNode(nodeId, nodeBodyUpdate, opts) {
        throwIfNotDefined(nodeId, 'nodeId');
        throwIfNotDefined(nodeBodyUpdate, 'nodeBodyUpdate');
        const pathParams = {
            nodeId
        };
        const queryParams = {
            include: buildCollectionParam(opts === null || opts === void 0 ? void 0 : opts.include, 'csv'),
            fields: buildCollectionParam(opts === null || opts === void 0 ? void 0 : opts.fields, 'csv')
        };
        return this.put({
            path: '/nodes/{nodeId}',
            pathParams,
            queryParams,
            bodyParam: nodeBodyUpdate,
            returnType: NodeEntry
        });
    }
    updateNodeContent(nodeId, contentBodyUpdate, opts) {
        throwIfNotDefined(nodeId, 'nodeId');
        throwIfNotDefined(contentBodyUpdate, 'contentBodyUpdate');
        opts = opts || {};
        const pathParams = {
            nodeId
        };
        const queryParams = {
            majorVersion: opts === null || opts === void 0 ? void 0 : opts.majorVersion,
            comment: opts === null || opts === void 0 ? void 0 : opts.comment,
            name: opts === null || opts === void 0 ? void 0 : opts.name,
            include: buildCollectionParam(opts === null || opts === void 0 ? void 0 : opts.include, 'csv'),
            fields: buildCollectionParam(opts === null || opts === void 0 ? void 0 : opts.fields, 'csv')
        };
        return this.put({
            path: '/nodes/{nodeId}/content',
            pathParams,
            queryParams,
            contentTypes: ['application/octet-stream'],
            bodyParam: contentBodyUpdate,
            returnType: NodeEntry
        });
    }
    requestDirectAccessUrl(nodeId) {
        throwIfNotDefined(nodeId, 'nodeId');
        const pathParams = {
            nodeId
        };
        return this.post({
            path: '/nodes/{nodeId}/request-direct-access-url',
            pathParams,
            returnType: DirectAccessUrlEntry
        });
    }
}
